import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Clock, Eye, CheckCircle2, XCircle } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { FilterPill } from '@/components/ui/FilterPill';
import { Button } from '@/components/ui/button';
import { useTrainingVideos } from '../hooks/useTrainingVideos';
import { ReviewRequestCard, getReviewStatus, ReviewStatus } from '../components/ReviewRequestCard';
import { TrainingVideo } from '@/types/videoAnnotations';
import { SOSSIETY_RED } from '@/styles/brand';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';

type TabKey = 'all' | ReviewStatus;

interface TabDef {
  key: TabKey;
  label: string;
  filter: (videos: TrainingVideo[]) => TrainingVideo[];
}

const EmptyState: React.FC<{
  tab: TabKey;
  onBrowse: () => void;
  t: ReturnType<typeof useI18n>['t'];
}> = ({ tab, onBrowse, t }) => {
  if (tab === 'all') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${SOSSIETY_RED}20` }}
        >
          <Video size={28} style={{ color: SOSSIETY_RED }} />
        </div>
        <div>
          <p className="text-white font-semibold text-lg">{t('my_reviews.empty_onboarding_title')}</p>
          <p className="text-white/50 text-sm mt-1">{t('my_reviews.empty_onboarding_description')}</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={onBrowse}
          className="mt-2"
        >
          {t('my_reviews.browse_instructors_button')}
        </Button>
      </div>
    );
  }

  type TabEmptyConfig = {
    icon: React.ReactNode;
    iconBg: string;
    title: string;
    description: string;
  };

  const tabConfig: Record<string, TabEmptyConfig> = {
    pending: {
      icon: <Clock size={32} className="text-amber-400" />,
      iconBg: 'bg-amber-400/10',
      title: t('my_reviews.empty_pending'),
      description: t('my_reviews.empty_pending_desc'),
    },
    in_review: {
      icon: <Eye size={32} className="text-blue-400" />,
      iconBg: 'bg-blue-400/10',
      title: t('my_reviews.empty_in_review'),
      description: t('my_reviews.empty_in_review_desc'),
    },
    delivered: {
      icon: <CheckCircle2 size={32} className="text-green-400" />,
      iconBg: 'bg-green-400/10',
      title: t('my_reviews.empty_delivered'),
      description: t('my_reviews.empty_delivered_desc'),
    },
    expired: {
      icon: <XCircle size={32} className="text-white/30" />,
      iconBg: 'bg-white/5',
      title: t('my_reviews.empty_expired'),
      description: t('my_reviews.empty_expired_desc'),
    },
  };

  const cfg = tabConfig[tab];
  if (!cfg) return null;

  return (
    <div className="flex flex-col items-center justify-center min-h-[52vh] text-center gap-5">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center ${cfg.iconBg}`}>
        {cfg.icon}
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-white text-base font-semibold">{cfg.title}</p>
        <p className="text-white/40 text-sm leading-relaxed max-w-[220px]">{cfg.description}</p>
      </div>
    </div>
  );
};

const MyReviews: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const { data: allVideos = [], isLoading } = useTrainingVideos();
  const [activeTab, setActiveTab] = useState<TabKey>('all');

  const TABS: TabDef[] = useMemo(
    () => [
      { key: 'all',       label: t('my_reviews.all_tab'),       filter: (v) => v },
      { key: 'pending',   label: t('my_reviews.pending_tab'),   filter: (v) => v.filter((x) => getReviewStatus(x) === 'pending') },
      { key: 'in_review', label: t('my_reviews.in_review_tab'), filter: (v) => v.filter((x) => getReviewStatus(x) === 'in_review') },
      { key: 'delivered', label: t('my_reviews.delivered_tab'), filter: (v) => v.filter((x) => getReviewStatus(x) === 'delivered') },
      { key: 'expired',   label: t('my_reviews.expired_tab'),   filter: (v) => v.filter((x) => getReviewStatus(x) === 'expired') },
    ],
    [t]
  );

  const myVideos = useMemo(
    () => allVideos.filter((v) => v.uploader_id === user?.id && v.status !== 'archived'),
    [allVideos, user?.id]
  );

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    TABS.forEach((tab) => { result[tab.key] = tab.filter(myVideos).length; });
    return result;
  }, [TABS, myVideos]);

  const visibleVideos = useMemo(
    () => TABS.find((tab) => tab.key === activeTab)!.filter(myVideos),
    [TABS, activeTab, myVideos]
  );

  return (
    <SubPageLayout title={t('my_reviews.title')}>

      {/* Filter pills */}
      <div className="flex gap-2 pt-4 pb-5 overflow-x-auto no-scrollbar">
        {TABS.map((tab) => (
          <FilterPill
            key={tab.key}
            label={tab.label}
            count={counts[tab.key]}
            active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-5 h-5 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
          </div>
        ) : visibleVideos.length === 0 ? (
          <EmptyState tab={activeTab} onBrowse={() => navigate('/browse-instructors')} t={t} />
        ) : (
          visibleVideos.map((video) => <ReviewRequestCard key={video.id} video={video} />)
        )}
      </div>

    </SubPageLayout>
  );
};

export default MyReviews;
